import { formatDate } from "date-fns"
import { Badge } from "design-system/atoms/Badge"
import { Tooltip, TooltipContent, TooltipTrigger } from "design-system/atoms/Tooltip"
import { cn } from "design-system/utils"
import { AmphoraIcon } from "modules/shared/icons/amphora"
import { FrontAppIcon } from "modules/shared/icons/frontapp"
import { GorgiasIcon } from "modules/shared/icons/gorgias"
import { ZendeskIcon } from "modules/shared/icons/zendesk"
import { ComponentType, SVGProps } from "react"
import { useTranslation } from "react-i18next"
import {
  ConversationTicketIntegrationKindEnum,
  ConversationTicketIntegrationResponseDataSchema,
} from "schema"
import { z } from "zod"

type Integration = z.infer<typeof ConversationTicketIntegrationResponseDataSchema>
type IntegrationKind = Integration["kind"]
type IntegrationIcon = ComponentType<SVGProps<SVGSVGElement>>

const INTEGRATION_ICONS: Record<IntegrationKind, IntegrationIcon> = {
  [ConversationTicketIntegrationKindEnum.Gorgias]: GorgiasIcon,
  [ConversationTicketIntegrationKindEnum.FrontApp]: FrontAppIcon,
  [ConversationTicketIntegrationKindEnum.Zendesk]: ZendeskIcon,
  [ConversationTicketIntegrationKindEnum.Amphora]: AmphoraIcon,
}

export function TicketIntegrationIcon({
  kind,
  className,
}: {
  kind: IntegrationKind
  className?: string
}) {
  const Icon = INTEGRATION_ICONS[kind]
  return <Icon className={className} />
}

export function TicketIntegrationBadges({
  integrations,
  tooltipKey = "support.tickets.escalatedThrough",
  className,
}: {
  integrations: Integration[]
  tooltipKey?: string
  className?: string
}) {
  const { t } = useTranslation()

  if (integrations.length === 0) return null

  return (
    <div className="flex flex-row gap-1.5">
      {integrations.map((integration) => (
        <Tooltip key={`${integration.kind}-${integration.externalId}`}>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <Badge variant="outline" className={cn(className)}>
                <TicketIntegrationIcon kind={integration.kind} className="size-4" />
                <span>{integration.kind}</span>
              </Badge>
            </span>
          </TooltipTrigger>
          <TooltipContent>
            {t(tooltipKey, {
              kind: integration.kind,
              createdAt: formatDate(integration.createdAt, "Pp"),
            })}
          </TooltipContent>
        </Tooltip>
      ))}
    </div>
  )
}
